import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import client from "../../api/client";

const HOMEPAGE_LIMIT = 5;
const inputCls = "w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30";

function FaqModal({ faq, categories, onClose, onSaved }) {
  const isEdit = !!faq;
  const [form, setForm] = useState({
    question: faq?.question || "",
    answer: faq?.answer || "",
    question_fr: faq?.question_fr || "",
    answer_fr: faq?.answer_fr || "",
    category: faq?.category || "general",
    is_active: faq?.is_active ?? true,
  });
  const [showFrench, setShowFrench] = useState(!!(faq?.question_fr || faq?.answer_fr));
  const [saving, setSaving] = useState(false);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        await client.put(`/admin/faqs/${faq.id}`, form);
        toast.success("FAQ updated.");
      } else {
        await client.post("/admin/faqs", form);
        toast.success("FAQ created.");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to save.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-bold text-gray-900 mb-5">{isEdit ? "Edit FAQ" : "New FAQ"}</h2>
        <div className="space-y-3">
          <input placeholder="Question" value={form.question} onChange={set("question")} className={inputCls} />
          <textarea rows={5} placeholder="Answer" value={form.answer} onChange={set("answer")} className={`${inputCls} resize-y`} />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-center">
            <select value={form.category} onChange={set("category")} className={inputCls}>
              {Object.entries(categories).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" checked={form.is_active}
                onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} className="rounded" />
              Visible on the website
            </label>
          </div>

          <button type="button" onClick={() => setShowFrench((v) => !v)}
            className="text-sm font-semibold text-[#294a93] hover:underline">
            {showFrench ? "− Hide French version" : "+ Add French version (optional)"}
          </button>
          {showFrench && (
            <div className="space-y-3 border-l-2 border-gray-100 pl-3">
              <p className="text-xs text-gray-400">Leave blank to show the English version on the French site.</p>
              <input placeholder="Question (French)" value={form.question_fr} onChange={set("question_fr")} className={inputCls} />
              <textarea rows={5} placeholder="Answer (French)" value={form.answer_fr} onChange={set("answer_fr")} className={`${inputCls} resize-y`} />
            </div>
          )}
        </div>
        <div className="flex gap-3 mt-6 justify-end">
          <button onClick={onClose} className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving || !form.question.trim() || !form.answer.trim()}
            className="px-5 py-2.5 rounded-xl bg-[#294a93] text-white text-sm font-semibold disabled:opacity-50 hover:bg-[#1e3a7a] transition-colors">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminFaqsPage() {
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | "new" | faq object
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    client.get("/admin/faqs")
      .then((res) => {
        setFaqs(res.data.data || []);
        setCategories(res.data.categories || {});
      })
      .catch(() => toast.error("Failed to load FAQs."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const move = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= faqs.length) return;
    const next = [...faqs];
    [next[index], next[target]] = [next[target], next[index]];
    const previous = faqs;
    setFaqs(next);
    try {
      await client.put("/admin/faqs/reorder", { ids: next.map((f) => f.id) });
    } catch {
      setFaqs(previous);
      toast.error("Could not save the new order.");
    }
  };

  const toggleActive = async (faq) => {
    setBusyId(faq.id);
    try {
      await client.put(`/admin/faqs/${faq.id}`, { ...faq, is_active: !faq.is_active });
      setFaqs((list) => list.map((f) => (f.id === faq.id ? { ...f, is_active: !f.is_active } : f)));
    } catch { toast.error("Update failed."); }
    finally { setBusyId(null); }
  };

  const remove = async (faq) => {
    if (!window.confirm(`Delete "${faq.question}"?`)) return;
    setBusyId(faq.id);
    try {
      await client.delete(`/admin/faqs/${faq.id}`);
      toast.success("Deleted.");
      setFaqs((list) => list.filter((f) => f.id !== faq.id));
    } catch { toast.error("Delete failed."); }
    finally { setBusyId(null); }
  };

  // Position among visible FAQs, so admins can see which ones reach the homepage.
  let visibleRank = 0;

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">FAQs</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            The first {HOMEPAGE_LIMIT} visible FAQs appear on the homepage; all of them appear on the FAQ page.
          </p>
        </div>
        <button onClick={() => setModal("new")}
          className="px-4 py-2.5 rounded-xl bg-[#294a93] text-white text-sm font-semibold hover:bg-[#1e3a7a] transition-colors">
          + New FAQ
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : faqs.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center text-sm text-gray-500">
          No FAQs yet. Click "New FAQ" to add one.
        </div>
      ) : (
        <div className="space-y-3">
          {faqs.map((faq, index) => {
            if (faq.is_active) visibleRank += 1;
            const onHomepage = faq.is_active && visibleRank <= HOMEPAGE_LIMIT;
            return (
              <div key={faq.id} className={`bg-white rounded-xl border border-gray-100 p-4 flex gap-3 ${faq.is_active ? "" : "opacity-60"}`}>
                <div className="flex flex-col gap-1 shrink-0">
                  <button onClick={() => move(index, -1)} disabled={index === 0}
                    className="w-7 h-7 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30" aria-label="Move up">↑</button>
                  <button onClick={() => move(index, 1)} disabled={index === faqs.length - 1}
                    className="w-7 h-7 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30" aria-label="Move down">↓</button>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{categories[faq.category] || faq.category}</span>
                    {onHomepage && <span className="text-xs bg-orange-50 text-[#d15424] px-2 py-0.5 rounded-full">On homepage</span>}
                    {!faq.is_active && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Hidden</span>}
                    {(faq.question_fr || faq.answer_fr) && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">FR</span>}
                  </div>
                  <p className="text-sm font-semibold text-gray-900">{faq.question}</p>
                  <p className="text-sm text-gray-500 mt-1 line-clamp-2">{faq.answer}</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 shrink-0 items-end sm:items-start">
                  <button onClick={() => toggleActive(faq)} disabled={busyId === faq.id}
                    className="text-xs font-semibold text-gray-600 px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50">
                    {faq.is_active ? "Hide" : "Show"}
                  </button>
                  <button onClick={() => setModal(faq)}
                    className="text-xs font-semibold text-[#294a93] px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50">Edit</button>
                  <button onClick={() => remove(faq)} disabled={busyId === faq.id}
                    className="text-xs font-semibold text-red-600 px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-red-50 disabled:opacity-50">Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <FaqModal
          faq={modal === "new" ? null : modal}
          categories={categories}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}
